// Configuração da API
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://analytics-api.cassio-de3.workers.dev';

class AnalyticsAPI {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Erro na requisição para ${endpoint}:`, error);
      throw error;
    }
  }

  // Overview - Métricas principais
  async getOverview(dateRange = '7daysAgo', compareRange = '14daysAgo') {
    const params = new URLSearchParams({
      range: dateRange,
      compare: compareRange
    });
    
    return await this.makeRequest(`/api/overview?${params}`);
  }

  // Traffic - Análise de tráfego
  async getTraffic(dateRange = '7daysAgo', dimension = 'source', limit = 10) {
    const params = new URLSearchParams({
      range: dateRange,
      dimension,
      limit: limit.toString()
    });
    
    return await this.makeRequest(`/api/traffic?${params}`);
  }

  // Conversions - Análise de conversões
  async getConversions(dateRange = '7daysAgo', eventName = null, limit = 10) {
    const params = new URLSearchParams({
      range: dateRange,
      limit: limit.toString()
    });
    
    if (eventName) {
      params.append('event', eventName);
    }
    
    return await this.makeRequest(`/api/conversions?${params}`);
  }

  // Realtime - Dados em tempo real
  async getRealtime(metric = 'activeUsers') {
    const params = new URLSearchParams({ metric });
    return await this.makeRequest(`/api/realtime?${params}`);
  }

  // Health check
  async checkHealth() {
    return await this.makeRequest('/api/health');
  }

  // Métodos auxiliares para cache local
  getCacheKey(endpoint, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {});
    
    return `analytics_${endpoint}_${JSON.stringify(sortedParams)}`;
  }

  // Cache simples no localStorage
  getCachedData(key, maxAge = 5 * 60 * 1000) { // 5 minutos padrão
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const data = JSON.parse(cached);
      const now = Date.now();
      
      if (now - data.timestamp > maxAge) {
        localStorage.removeItem(key);
        return null;
      }

      return data.value;
    } catch (error) {
      console.error('Erro ao ler cache:', error);
      return null;
    }
  }

  setCachedData(key, value) {
    try {
      const cacheData = {
        value,
        timestamp: Date.now()
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Erro ao salvar cache:', error);
    }
  }

  // Wrapper com cache automático
  async getWithCache(endpoint, params = {}, maxAge = 5 * 60 * 1000) {
    const cacheKey = this.getCacheKey(endpoint, params);
    
    // Tentar buscar do cache primeiro
    const cached = this.getCachedData(cacheKey, maxAge);
    if (cached) {
      return { ...cached, fromCache: true };
    }

    // Se não está em cache, fazer requisição
    let data;
    switch (endpoint) {
      case 'overview':
        data = await this.getOverview(params.range, params.compare);
        break;
      case 'traffic':
        data = await this.getTraffic(params.range, params.dimension, params.limit);
        break;
      case 'conversions':
        data = await this.getConversions(params.range, params.event, params.limit);
        break;
      case 'realtime':
        data = await this.getRealtime(params.metric);
        break;
      default:
        throw new Error(`Endpoint desconhecido: ${endpoint}`);
    }

    // Salvar no cache
    this.setCachedData(cacheKey, data);
    
    return { ...data, fromCache: false };
  }
}

// Instância singleton
export const analyticsAPI = new AnalyticsAPI();

// Exports nomeados para flexibilidade
export { AnalyticsAPI };

// Utilitários para formatação de dados
export const formatters = {
  number: (value) => {
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    }
    if (value >= 1000) {
      return (value / 1000).toFixed(1) + 'K';
    }
    return value.toLocaleString();
  },

  percentage: (value) => {
    return parseFloat(value).toFixed(2) + '%';
  },

  duration: (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  },

  currency: (value, currency = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency
    }).format(value);
  },

  date: (date) => {
    return new Date(date).toLocaleDateString('pt-BR');
  },

  dateTime: (date) => {
    return new Date(date).toLocaleString('pt-BR');
  }
}; 